import { useMemo } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Users,
  ChevronLeft,
  RefreshCw,
  ArrowRight,
  FolderOpen,
  LayoutGrid,
  AlertCircle,
  Layers,
} from "lucide-react";

import { PERMS } from "../../../utils/perms";
import PageGate from "../../../components/PageGate";
import {
  useGetActiveUserCategoriesQuery,
  useGetDietCategoryCountsQuery,
} from "../api/probationerApplicationApi";

/**
 * Which category's review assignment to work.
 *
 * Review Assignment used to open on the whole diet at once — every
 * probationer, student and technician in one pool, one assigned table and one
 * set of panel load counts. But the committee works a diet one category at a
 * time, so the first thing anybody did on that screen was filter it, and the
 * panel's counts described work nobody was looking at.
 *
 * So the press lands here instead: pick the category, and the screen that
 * opens is scoped to it — pool, assigned table, panel load, analysis and
 * exports all about that category alone.
 *
 * The three categories are the ones the entry route admits under: the same
 * keyword filter the diet's category screen uses, so the two lists cannot
 * disagree about which categories a diet has.
 */

const ALLOWED_KEYWORDS = ["probationer", "student", "technician"];

function safeMsg(x: any) {
  return String(
    x?.data?.message || x?.message || x?.error || "Something went wrong",
  );
}

function CategoryCard({
  id,
  name,
  dietId,
  count,
}: {
  id: number;
  name: string;
  dietId: string;
  count: number;
}) {
  return (
    <Link
      to={`/probationer-applications/admin/submissions/diets/${dietId}/assignments/category/${id}`}
      state={{ categoryName: name, categoryId: id, dietId }}
      className="group relative bg-white border border-gray-200 rounded-[32px] p-8 transition-all hover:shadow-2xl hover:shadow-gray-200 hover:-translate-y-1 overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-24 h-24 bg-gray-50 rounded-bl-[60px] -mr-8 -mt-8 transition-colors group-hover:bg-blue-50" />
      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-start justify-between mb-6">
          <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all shadow-sm">
            <FolderOpen className="w-6 h-6" />
          </div>
          <div className="text-right">
            <div className="text-3xl font-black text-gray-900 leading-none tabular-nums">
              {count}
            </div>
            <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-1">
              Application{count === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        <h3 className="text-xl font-black text-gray-900 tracking-tight mb-2 group-hover:text-blue-600 transition-colors">
          {name}
        </h3>
        <p className="text-sm font-medium text-gray-500 leading-relaxed mb-6">
          Place this category's applications with the Membership Committee, and
          see who is carrying what.
        </p>
        <div className="mt-auto pt-6 border-t border-gray-50 flex items-center justify-between">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 group-hover:text-gray-900 transition-colors">
            Assign this category
          </span>
          <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all">
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function AdminProbationerReviewAssignmentCategoriesPage() {
  const { dietId = "" } = useParams<{ dietId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    data: categories = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetActiveUserCategoriesQuery();

  const { data: catCounts } = useGetDietCategoryCountsQuery(
    { dietId },
    { skip: !dietId },
  );
  const counts = catCounts?.counts ?? {};

  const list = useMemo(
    () =>
      (Array.isArray(categories) ? categories : []).filter((c: any) =>
        ALLOWED_KEYWORDS.some((kw) => c.name?.toLowerCase().includes(kw)),
      ),
    [categories],
  );

  const diet = catCounts?.diet;
  const dietTitle =
    diet?.title || (location.state as any)?.dietTitle || "Diet System Record";

  return (
    <PageGate perm={PERMS.application_diet.allCategoriesAccess}>
      <div className="max-w-7xl mx-auto p-6 space-y-8 pb-20">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="group flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-colors"
            >
              <div className="p-2 rounded-lg bg-white border border-gray-100 group-hover:border-gray-900 transition-all">
                <ChevronLeft className="w-4 h-4" />
              </div>
              Back to Diet Categories
            </button>

            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="p-2.5 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 transition-all shadow-sm"
            >
              <RefreshCw
                className={`w-4 h-4 text-gray-500 ${
                  isFetching ? "animate-spin" : ""
                }`}
              />
            </button>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-white border border-gray-100 p-6 rounded-[32px] shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div className="space-y-1">
                <h1 className="text-3xl font-black text-gray-900 tracking-tight">
                  Review Assignment
                </h1>
                <p className="text-sm font-bold text-gray-500">
                  {dietTitle} · Pick the category to assign
                </p>
              </div>
            </div>

            {/* The old diet-wide screen, still reachable. Some questions —
                "who on the panel is overloaded across the whole diet" — are
                about the diet rather than a category, and the links into this
                screen from elsewhere in the module still point there. */}
            <Link
              to={`/probationer-applications/admin/submissions/diets/${dietId}/assignments`}
              className="self-start lg:self-auto flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-[11px] font-bold uppercase tracking-tight text-gray-700 transition-all"
            >
              <LayoutGrid className="w-4 h-4 text-gray-400" />
              All categories at once
            </Link>
          </div>
        </div>

        {error ? (
          <div className="p-4 rounded-[24px] border bg-red-50 border-red-100 text-red-800 flex items-center gap-3">
            <AlertCircle className="w-5 h-5" />
            <div className="text-sm font-bold tracking-tight">
              {safeMsg(error)}
            </div>
          </div>
        ) : null}

        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-64 bg-gray-50 border border-gray-100 rounded-[32px] animate-pulse"
              />
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="py-20 bg-gray-50 border-2 border-dashed border-gray-200 rounded-[40px] flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-white rounded-3xl flex items-center justify-center shadow-sm mb-4">
              <LayoutGrid className="w-8 h-8 text-gray-300" />
            </div>
            <h2 className="text-xl font-black text-gray-900 uppercase">
              Nothing to assign
            </h2>
            <p className="text-gray-500 max-w-xs mx-auto mt-2 font-medium">
              No active entry categories were found.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((c: any) => (
              <CategoryCard
                key={c.id}
                id={c.id}
                name={c.name}
                dietId={dietId}
                count={Number(counts[String(c.id)] ?? 0)}
              />
            ))}
          </div>
        )}

        <div className="mt-12 p-8 bg-blue-50/50 rounded-[32px] border border-blue-100 flex items-start gap-4">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm shrink-0">
            <Layers className="text-blue-600 w-5 h-5" />
          </div>
          <div>
            <h4 className="text-xs font-black text-blue-900 uppercase tracking-widest mb-1">
              Administrative Note
            </h4>
            <p className="text-xs font-bold text-blue-700/70 leading-relaxed max-w-2xl">
              The count on each card is every application in that category for
              this diet. What can actually be handed out is narrower — the
              application fee must be settled, the chapter must have endorsed
              and both referees must have approved. The category's own screen
              names whatever is holding each one back.
            </p>
          </div>
        </div>
      </div>
    </PageGate>
  );
}
